// Saved cards — the section a parent uses to see and replace the card that
// renews their subscription. M7e §1.1A.2.
//
// The API shipped in M7c and nothing consumed it: until now a parent could not
// see their own card, and the seeded past-due / card-expiring emails linked at
// an update flow with no screen behind it.
//
// 🔴 `isExpired` / `isExpiringSoon` are computed SERVER-side and read straight
// off the DTO. Never re-derive expiry here from expMonth/expYear — the server
// owns what "today" means for billing.

import { useT } from '@fajrarabi/i18n/react';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Dialog,
  DialogContent,
  Skeleton,
} from '@fajrarabi/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '~/lib/api';
import {
  listPaymentMethods,
  makeDefaultPaymentMethod,
  removePaymentMethod,
  startCardUpdate,
  type PaymentMethod,
} from '~/lib/billing';

export function PaymentMethodsCard({ hasSubscription }: { hasSubscription: boolean }) {
  const t = useT();
  const qc = useQueryClient();
  const [pendingRemoval, setPendingRemoval] = useState<PaymentMethod | null>(null);

  const cardsQuery = useQuery({
    queryKey: ['billing', 'payment-methods'],
    queryFn: ({ signal }) => listPaymentMethods(signal),
    enabled: hasSubscription,
  });

  const update = useMutation({
    mutationFn: () => startCardUpdate(),
    onSuccess: (checkoutUrl) => {
      window.location.href = checkoutUrl;
    },
  });

  const makeDefault = useMutation({
    mutationFn: (id: string) => makeDefaultPaymentMethod(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['billing', 'payment-methods'] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => removePaymentMethod(id),
    onSuccess: () => {
      setPendingRemoval(null);
      void qc.invalidateQueries({ queryKey: ['billing', 'payment-methods'] });
    },
  });

  // No subscription at all → the whole section is noise. §1.1A.2.
  if (!hasSubscription) return null;

  const cards = cardsQuery.data ?? [];
  const busy = update.isPending || makeDefault.isPending || remove.isPending;
  const mutationError = [update.error, makeDefault.error, remove.error].find(Boolean);

  return (
    <section className="mt-10" aria-labelledby="payment-method-heading">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="payment-method-heading" className="text-lg font-semibold text-ink-900">
          {t('app.billing.payment_method')}
        </h2>
        <Button size="sm" variant="secondary" onClick={() => update.mutate()} disabled={busy}>
          {t('app.billing.card_update')}
        </Button>
      </div>

      {mutationError && (
        <Alert tone="danger" className="mt-4">
          {mutationError instanceof ApiError
            ? mutationError.message
            : t('app.common.error_generic')}
        </Alert>
      )}

      <div className="mt-4">
        {cardsQuery.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : cards.length === 0 ? (
          // A live subscription with no card on file is a dunning risk, not a
          // neutral empty state — say so with a warning, per §1.1A.2.
          <Alert tone="warning">{t('app.billing.card_none_warning')}</Alert>
        ) : (
          <ul className="space-y-3">
            {cards.map((card) => (
              <li key={card.id}>
                <CardRow
                  card={card}
                  busy={busy}
                  onMakeDefault={() => makeDefault.mutate(card.id)}
                  onRemove={() => setPendingRemoval(card)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      <RemoveCardDialog
        card={pendingRemoval}
        busy={remove.isPending}
        onCancel={() => setPendingRemoval(null)}
        onConfirm={(id) => remove.mutate(id)}
      />
    </section>
  );
}

function brandLabel(card: PaymentMethod, fallback: string): string {
  return card.brand ?? fallback;
}

function CardRow({
  card,
  busy,
  onMakeDefault,
  onRemove,
}: {
  card: PaymentMethod;
  busy: boolean;
  onMakeDefault: () => void;
  onRemove: () => void;
}) {
  const t = useT();
  const brand = brandLabel(card, t('app.billing.card_brand_unknown'));

  return (
    <Card>
      <CardBody>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-semibold text-ink-900">
                {t('app.billing.card_last4', { brand, last4: card.last4 ?? '••••' })}
              </p>
              {card.isDefault && <Badge tone="brand">{t('app.billing.card_default')}</Badge>}
              {card.isExpired ? (
                <Badge tone="danger">{t('app.billing.card_expired')}</Badge>
              ) : card.isExpiringSoon ? (
                <Badge tone="warning">{t('app.billing.card_expiring_soon')}</Badge>
              ) : null}
            </div>
            {card.expMonth != null && card.expYear != null && (
              <p className="mt-1 text-sm text-ink-500 tabular-nums">
                {t('app.billing.card_expires', {
                  month: String(card.expMonth).padStart(2, '0'),
                  year: card.expYear,
                })}
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {!card.isDefault && (
              <Button size="sm" variant="ghost" onClick={onMakeDefault} disabled={busy}>
                {t('app.billing.card_make_default')}
              </Button>
            )}
            {/* Shown even when it is the only card under an active paid
                subscription: the API refuses that removal and the app surfaces
                its message. Hiding a control with no explanation reads as a
                bug. §8.5. */}
            <Button size="sm" variant="ghost" onClick={onRemove} disabled={busy}>
              {t('app.billing.card_remove')}
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function RemoveCardDialog({
  card,
  busy,
  onCancel,
  onConfirm,
}: {
  card: PaymentMethod | null;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (id: string) => void;
}) {
  const t = useT();
  const brand = card ? brandLabel(card, t('app.billing.card_brand_unknown')) : '';

  return (
    <Dialog open={card !== null} onOpenChange={(open) => !open && onCancel()}>
      {card && (
        <DialogContent
          title={t('app.billing.card_remove_confirm', { brand, last4: card.last4 ?? '••••' })}
          description={t('app.billing.card_remove_confirm_body')}
        >
          <div className="mt-2 flex justify-end gap-2">
            <Button variant="ghost" onClick={onCancel} disabled={busy}>
              {t('app.common.cancel')}
            </Button>
            <Button variant="primary" onClick={() => onConfirm(card.id)} disabled={busy}>
              {t('app.billing.card_remove')}
            </Button>
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
}
